import { useState, useEffect } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { format } from 'date-fns';
import apiClient from './api/client';
import Select from 'react-select';
import UserDetailsModal from './components/UserDetailsModal';
import ChangePasswordModal from './components/ChangePasswordModal';
import ShowPasswordModal from './components/ShowPasswordModal';
import AdvanceFeatureModal from './components/AdvanceFeatureModal';
import DoctorInfoModal from './components/DoctorInfoModal';
import { useCompanies, useRoles, useProviders } from './hooks';
import { getRolesByProvider, getRolesByCompany } from './services/masterDataService';
import { useCurrentDomain } from "./context/DomainContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ChevronUp, ChevronDown, Search, RotateCcw, Eye } from 'lucide-react';

const BRAND_GREEN = '#0aa375';

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '30px',
    height: '30px',
    fontSize: '12px',
    borderColor: '#dee2e6',
    boxShadow: 'none',
    '&:hover': { borderColor: BRAND_GREEN },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, padding: '0 2px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
  option: (base, state) => ({
    ...base,
    fontSize: '12px',
    backgroundColor: state.isSelected ? BRAND_GREEN : state.isFocused ? '#e6f7f2' : 'white',
    color: state.isSelected ? 'white' : '#333',
  }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
};

const pageSizeOptions = [10, 25, 50, 100].map((n) => ({ value: n, label: `${n} / page` }));

export default function ManageRegistration() {
  const { domain } = useCurrentDomain();

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [username, setUsername] = useState('');
  const [createdAfter, setCreatedAfter] = useState('');

  const [users, setUsers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [showShowModal, setShowShowModal] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [showDoctorInfoModal, setShowDoctorInfoModal] = useState(false);
  const [providerRoles, setProviderRoles] = useState([]);
  const [providerRolesLoading, setProviderRolesLoading] = useState(false);
  const [companyRoles, setCompanyRoles] = useState([]);
  const [companyRolesLoading, setCompanyRolesLoading] = useState(false);
  const { companies, loading: companiesLoading } = useCompanies(domain);
  const { providers, loading: providersLoading } = useProviders(domain, selectedCompany?.value ?? "-1");
  const { roles, loading: rolesLoading } = useRoles(domain);

  const providerOptions = [...providers]
    .map((p) => ({
      value: p.id || p.text || '',
      label: p.text || p.id || '(No Provider)',
      officeId: p.id || p.text || '',
    }))
    .sort((a, b) => {
      if (selectedProvider?.value && a.value === selectedProvider.value) return -1;
      if (selectedProvider?.value && b.value === selectedProvider.value) return 1;
      return 0;
    });

  const roleSource = selectedCompany?.value ? companyRoles : (roles || []);
  const roleOptions = roleSource.map(role => ({
    value: role.id,
    label: role.text || role.sz_user_role || ''
  }));

  const companyOptions = (companies || [])
  .map((c) => ({
    ...c,
    label: [c.companyName, c.aliasName, c.companyId || c.value]
      .filter(Boolean)
      .join(' - '),
  }))
  .sort((a, b) => {
    if (selectedCompany?.value && a.value === selectedCompany.value) return -1;
    if (selectedCompany?.value && b.value === selectedCompany.value) return 1;
    return 0;
  });

  const filterCompanyOption = (candidate, input) => {
    if (!input) return true;
    const term = input.toLowerCase();
    const d = candidate.data || {};
    return [d.companyName, d.aliasName, d.companyId || candidate.value, candidate.label]
      .some((v) => (v || '').toLowerCase().includes(term));
  };

  const allowedDomainsForManageRegistration = ['greenyourbills.com', 'ab.greenyourbills.com'];
  const hasManageRegistrationAccess = allowedDomainsForManageRegistration.includes(domain?.toLowerCase() || '');
  const isLoading = companiesLoading || rolesLoading || providersLoading;

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const [showFilters, setShowFilters] = useState(true);

  const handleSearch = async (pageOverride, pageSizeOverride) => {
    setSearchLoading(true);
    setError(null);
    const currentPage = pageOverride ?? page;
    const currentPageSize = pageSizeOverride ?? pageSize;
    try {
      const payload = {
        domain: domain || undefined,
        companyId: selectedCompany?.value || undefined,
        provider: selectedProvider?.value || undefined,
        username: username.trim() || undefined,
        role: selectedRole?.label || selectedRole?.text || undefined,
        createdAfter: createdAfter || undefined,
        page: currentPage,
        pageSize: currentPageSize,
      };
      const res = await apiClient.post(`/api/users/search`, payload);
      setUsers(res.data.items || []);
      setTotalCount(res.data.totalCount ?? res.data.items?.length ?? 0);
      setPage(currentPage);
      setPageSize(currentPageSize);
      if ((res.data.items || []).length === 0) toast('No matching users found');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to search users';
      setError(msg);
      toast.error(msg);
    } finally {
      setSearchLoading(false);
    }
  };

  const goToPage = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page || searchLoading) return;
    handleSearch(newPage);
  };

  const handlePageSizeChange = (opt) => {
    handleSearch(1, opt.value);
  };

  const Field = ({ label}) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
        {label}
      </span>
    </div>
  );

  const handleReset = () => {
    setSelectedCompany(null);
    setSelectedProvider(null);
    setSelectedRole(null);
    setUsername('');
    setCreatedAfter('');
    setUsers([]);
    setError(null);
    setPage(1);
    setTotalCount(0);
  };

  useEffect(() => {
    if (!selectedCompany?.value) {
      setSelectedProvider(null);
      setSelectedRole(null);
    }
  }, [selectedCompany]);

  useEffect(() => {
    const companyId = selectedCompany?.value;
    if (!companyId || !domain) {
      setCompanyRoles([]);
      setCompanyRolesLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setCompanyRolesLoading(true);
      try {
        const data = await getRolesByCompany(domain, companyId);
        if (!cancelled) setCompanyRoles(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) {
          setCompanyRoles([]);
          toast.error('Failed to load roles for company');
        }
      } finally {
        if (!cancelled) setCompanyRolesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedCompany?.value, domain]);

  useEffect(() => {
    setSelectedRole(null);
  }, [selectedCompany?.value]);

  useEffect(() => {
    setSelectedRole(null);
  }, [selectedProvider?.officeId]);

  useEffect(() => {
    handleReset();
  }, [domain]);

  const updateUserInList = (userId, field, newValue) => {
    setUsers(prev => prev.map(u => u.szUserId === userId ? { ...u, [field]: newValue } : u));
  };

  const handleAction = async (action, user) => {
    if (action === 'copy_pwd') {
      try {
        const toastId = toast.loading("Fetching password...");
        const res = await apiClient.post(`/api/users/contact-details`, {
          domain, userId: user.szUserId, companyId: user.szCompanyId,
        });
        const plainPassword = res.data.plainPassword;
        if (!plainPassword || plainPassword === '[Decryption failed]') {
          toast.error("Could not retrieve password", { id: toastId });
          return;
        }
        await navigator.clipboard.writeText(plainPassword);
        toast.success("Password copied!", { id: toastId });
      } catch {
        toast.error("Failed to copy password");
      }
    } else if (action === 'change_pwd') {
      setSelectedUser(user); setShowChangeModal(true);
    } else if (action === 'show_pwd') {
      setSelectedUser(user); setShowShowModal(true);
    } else if (action === 'enable_login' || action === 'disable_login') {
      const newVal = !user.btIsDisabled;
      updateUserInList(user.szUserId, 'btIsDisabled', newVal);
      try {
        let actionName = action === 'enable_login' ? 'enabled' : 'disabled';
        await apiClient.post(`/api/users/toggle-login`, { actionName, domain, userId: user.szUserId, companyId: user.szCompanyId });
        toast.success(`Login ${actionName}`);
      } catch {
        updateUserInList(user.szUserId, 'btIsDisabled', user.btIsDisabled);
        toast.error("Failed to toggle login status");
      }
    } else if (action === 'enable_otp' || action === 'disable_otp') {
      const newVal = !user.btIsOtpDisabled;
      updateUserInList(user.szUserId, 'btIsOtpDisabled', newVal);
      try {
        let actionName = action === 'enable_otp' ? 'enabled' : 'disabled';
        await apiClient.post(`/api/users/toggle-otp`, { actionName, domain, userId: user.szUserId, companyId: user.szCompanyId });
        toast.success(`OTP ${actionName}`);
      } catch {
        updateUserInList(user.szUserId, 'btIsOtpDisabled', user.btIsOtpDisabled);
        toast.error("Failed to toggle OTP");
      }
    } else if (action === 'enable_msg' || action === 'disable_msg') {
      const newVal = !user.btShowLoginMessage;
      updateUserInList(user.szUserId, 'btShowLoginMessage', newVal);
      try {
        let actionName = action === 'enable_msg' ? 'enabled' : 'disabled';
        await apiClient.post(`/api/users/toggle-login-message`, { actionName, domain, userId: user.szUserId, companyId: user.szCompanyId });
        toast.success(`Login message ${actionName}`);
      } catch {
        updateUserInList(user.szUserId, 'btShowLoginMessage', user.btShowLoginMessage);
        toast.error("Failed to toggle login message");
      }
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <style>{`
        .react-datepicker-popper, #datepicker-portal { z-index: 1055 !important; }
        .tbl-compact td, .tbl-compact th { padding: 4px 8px !important; vertical-align: middle; }
        .tbl-compact thead th { background: #f8f9fa; font-size: 11.5px; font-weight: 600; color: #495057; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 1px solid #dee2e6; white-space: nowrap; }
        .tbl-compact tbody tr { height: 36px; }
        .tbl-compact tbody tr:hover { background-color: #f0fdf8; }
        .action-btn { font-size: 11px !important; padding: 2px 8px !important; min-width: 56px; font-weight: 600; border: none; border-radius: 3px; cursor: pointer; color: white; }
        .action-btn.green { background-color: #0aa375; }
        .action-btn.red { background-color: #dc3545; }
        .link-action { color: #0aa375 !important; font-size: 11.5px; text-decoration: none; background: none; border: none; padding: 0; cursor: pointer; }
        .link-action:hover { text-decoration: underline; color: #088a63 !important; }
        .sep { color: #ced4da; margin: 0 3px; font-size: 10px; }
        .form-control-xs { height: 30px !important; font-size: 12px !important; padding: 2px 8px !important; }
        .filter-label { font-size: 11px; font-weight: 600; color: #6c757d; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px; }
        .pagination-btn { font-size: 11px !important; padding: 2px 10px !important; }
        @media (max-width: 768px) { .row.g-2 > div { margin-bottom: 8px; } .tbl-compact td, .tbl-compact th { padding: 8px 6px !important; font-size: 11.5px; } .action-btn, .link-action { padding: 3px 7px !important; font-size: 11px !important; } }
      `}</style>

      <div className="min-vh-100 bg-white">
        <div className="card shadow-sm border-0">
          {showFilters && (
          <div className="card-body py-2 px-3">
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-3">
                <Field label="Company" />
                <Select
                  options={companyOptions}
                  value={selectedCompany}
                  onChange={setSelectedCompany}
                  filterOption={filterCompanyOption}
                  placeholder="Select company..."
                  isClearable isSearchable
                  isDisabled={companiesLoading}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-3">
                <Field label="Provider" />
                <Select
                  options={providerOptions}
                  value={selectedProvider}
                  onChange={setSelectedProvider}
                  placeholder="Select provider..."
                  isClearable isSearchable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-2">
                <Field label="Role" />
                <Select
                  options={roleOptions}
                  value={selectedRole}
                  onChange={setSelectedRole}
                  isDisabled={companyRolesLoading}
                  placeholder={selectedCompany?.value ? 'Roles for company...' : 'Select role...'}
                  isClearable isSearchable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-2">
                <Field label="Username" />
                <input
                  type="text"
                  className="form-control form-control-xs"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username..."
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
                />
              </div>
              <div className="col-12 col-md-2">
                <Field label="Created After" />
                <DatePicker
                  selected={createdAfter ? new Date(createdAfter) : null}
                  onChange={(date) => setCreatedAfter(date ? format(date, 'yyyy-MM-dd') : '')}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/DD/YYYY"
                  className="form-control form-control-xs"
                  wrapperClassName="w-100"
                  portalId="datepicker-portal"
                  popperClassName="react-datepicker-popper"
                  isClearable showMonthDropdown showYearDropdown dropdownMode="select"
                />
              </div>
            </div>

           <div className="d-flex justify-content-center gap-2 mt-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                style={{
                  fontSize: '12px',
                  height: '28px',
                  width: window.innerWidth < 768 ? '36px' : 'auto',
                  padding: window.innerWidth < 768 ? '0' : '2px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={handleReset}
                disabled={searchLoading || isLoading || !hasManageRegistrationAccess}
                title="Reset"
              >
                {window.innerWidth < 768 ? <RotateCcw size={15} /> : 'Reset'}
              </button>
              <button
                className="btn btn-sm text-white fw-semibold"
                style={{
                  backgroundColor: (searchLoading || isLoading || !hasManageRegistrationAccess) ? '#6c757d' : BRAND_GREEN,
                  border: 'none',
                  fontSize: '12px',
                  height: '28px',
                  width: window.innerWidth < 768 ? '36px' : 'auto',
                  padding: window.innerWidth < 768 ? '0' : '2px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => handleSearch(1)}
                disabled={searchLoading || isLoading || !hasManageRegistrationAccess}
                title="Search"
              >
                {searchLoading ? (
                  <span className="spinner-border spinner-border-sm" role="status" />
                ) : window.innerWidth < 768 ? (
                  <Search size={15} />
                ) : (
                  'Search'
                )}
              </button>
            </div>

            {!hasManageRegistrationAccess && domain && (
              <div className="alert alert-danger mt-2 mb-0 py-2 text-center" style={{ fontSize: '12px' }}>
                <i className="fas fa-exclamation-triangle me-1"></i>
                The selected domain doesn't have Manage Registration feature.
              </div>
            )}
            {error && (
              <div className="alert alert-danger mt-2 mb-0 py-2" style={{ fontSize: '12px' }}>{error}</div>
            )}
            <button
              className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 d-md-none mt-2"
              style={{ height: '28px', fontSize: '11px', whiteSpace: 'nowrap' }}
              onClick={() => setShowFilters(false)}
            >
              Hide Filters <ChevronUp size={14} />
            </button>
          </div>
        )}

          <hr className="my-0" />

          {hasManageRegistrationAccess && (
            <div className="card-body p-0">
              <div className="px-3 py-1 border-bottom bg-light d-flex align-items-center justify-content-between">
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#495057' }}>
                  User records ({totalCount})
                </span>
                {!showFilters && (
                  <button
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 d-md-none"
                    style={{ fontSize: '11px', height: '24px', padding: '0 10px' }}
                    onClick={() => setShowFilters(true)}
                  >
                    Show Filters <ChevronDown size={14} />
                  </button>
                )}
              </div>

              {searchLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-success spinner-border-sm" role="status" />
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-4 text-muted" style={{ fontSize: '13px' }}>
                  No records to display.
                </div>
              ) : (
                <>
                  <div className="table-responsive" style={{ maxHeight: '65vh', overflowY: 'auto', overflowX: 'auto' }}>
                    {/* Desktop Table */}
                    <table className="table table-hover mb-0 tbl-compact d-none d-md-table" style={{ tableLayout: 'fixed', minWidth: '1100px' }}>
                      <thead className="sticky-top">
                        <tr>
                          <th style={{ width: '20%' }}>Account</th>
                          <th style={{ width: '12%' }}>User</th>
                          <th style={{ width: '10%' }}>Role</th>
                          <th style={{ width: '12%' }}>Password</th>
                          <th style={{ width: '10%' }}>Domain</th>
                          <th style={{ width: '13%' }}>Date Created</th>
                          <th style={{ width: '13%' }}>Last Login</th>
                          <th style={{ width: '65px' }} className="text-center">Login</th>
                          <th style={{ width: '65px' }} className="text-center">OTP</th>
                          <th style={{ width: '65px' }} className="text-center">Msg</th>
                          <th style={{ width: '75px' }} className="text-center">Advance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.szUserId}>
                            <td style={{ fontSize: '12px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                              {user.szCompanyName || '—'}
                            </td>
                            <td>
                              <button
                                className="link-action"
                                onClick={() => { setSelectedUser(user); setShowUserModal(true); }}
                              >
                                {user.szUserName || '—'}
                              </button>
                            </td>
                            <td style={{ fontSize: '12px' }}>
                              {(user.szUserRole?.toLowerCase().includes('doctor') || user.szUserRole?.toLowerCase().includes('doctors')) ? (
                                <button
                                  className="link-action"
                                  style={{ textDecoration: 'underline', fontWeight: 500 }}
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setShowDoctorInfoModal(true);
                                  }}
                                  title="Click to view doctor details"
                                >
                                  {user.szUserRole}
                                </button>
                              ) : (
                                user.szUserRole || '—'
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-1 flex-wrap" style={{ fontSize: '11.5px' }}>
                                <button className="link-action" onClick={() => handleAction('copy_pwd', user)}>Copy</button>
                                <span className="sep">|</span>
                                <button className="link-action" onClick={() => handleAction('change_pwd', user)}>Change</button>
                                <span className="sep">|</span>
                                <button className="link-action" onClick={() => handleAction('show_pwd', user)}>Show</button>
                              </div>
                            </td>
                            <td style={{ fontSize: '12px' }}>{user.szDomainName || '—'}</td>
                            <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                              {user.dtCreated ? format(new Date(user.dtCreated), 'dd-MMM-yy hh:mm a') : '—'}
                            </td>
                            <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                              {user.dT_LAST_LOGIN ? format(new Date(user.dT_LAST_LOGIN), 'dd-MMM-yy hh:mm a') : '—'}
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm text-white"
                                style={{ backgroundColor: user.btIsDisabled ? '#0aa375' : 'red', border: 'none', minWidth: '58px', fontSize: '11px', padding: '2px 0' }}
                                onClick={() => handleAction(user.btIsDisabled ? 'enable_login' : 'disable_login', user)}
                              >
                                {user.btIsDisabled ? 'Enable' : 'Disable'}
                              </button>
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm text-white"
                                style={{ backgroundColor: user.btIsOtpDisabled ? 'red' : '#0aa375', border: 'none', minWidth: '58px', fontSize: '11px', padding: '2px 0' }}
                                onClick={() => handleAction(user.btIsOtpDisabled ? 'enable_otp' : 'disable_otp', user)}
                              >
                                {user.btIsOtpDisabled ? 'Disable' : 'Enable'}
                              </button>
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm text-white"
                                style={{ backgroundColor: user.btShowLoginMessage ? 'red' : '#0aa375', border: 'none', minWidth: '58px', fontSize: '11px', padding: '2px 0' }}
                                onClick={() => handleAction(user.btShowLoginMessage ? 'disable_msg' : 'enable_msg', user)}
                              >
                                {user.btShowLoginMessage ? 'Disable' : 'Enable'}
                              </button>
                            </td>
                            <td className="text-center">
                              {user.szUserRole?.toLowerCase() === 'doctor' && (
                                <button
                                  className="link-action"
                                  style={{ fontSize: '11.5px' }}
                                  onClick={() => { setSelectedUser(user); setShowAdvanceModal(true); }}
                                >
                                  Advance Feature
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Card View */}
                    <div className="d-md-none">
                      {users.map((user) => (
                        <div key={user.szUserId} style={{
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '16px 14px 14px',
                          marginBottom: '12px',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                          position: 'relative'
                        }}>
                          <button
                            className="link-action"
                            onClick={() => { setSelectedUser(user); setShowUserModal(true); }}
                            style={{ 
                              position: 'absolute', 
                              top: '12px', 
                              right: '14px', 
                              fontSize: '13px',
                              fontWeight: 500
                            }}
                          >
                            <Eye size={16} />
                          </button>
                          <div style={{ marginBottom: '8px', paddingRight: '70px' }}>
                            <strong>Account:</strong> {user.szCompanyName || '—'}
                          </div>
                          <div style={{ marginBottom: '8px' }}>
                            <strong>USER:</strong> {user.szUserName || '—'}
                          </div>
                          <div style={{ marginBottom: '8px' }}>
                            <strong>ROLE:</strong> {user.szUserRole || '—'}
                          </div>
                          <div style={{ marginBottom: '8px' }}>
                            <strong>Password:</strong>{' '}
                            <div className="d-flex align-items-center gap-1 flex-wrap" style={{ display: 'inline-flex' }}>
                              <button className="link-action" onClick={() => handleAction('copy_pwd', user)} style={{ fontSize: '14px' }}>Copy</button>
                              <span className="sep">|</span>
                              <button className="link-action" onClick={() => handleAction('change_pwd', user)} style={{ fontSize: '14px' }}>Change</button>
                              <span className="sep">|</span>
                              <button className="link-action" onClick={() => handleAction('show_pwd', user)} style={{ fontSize: '14px' }}>Show</button>
                            </div>
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>Domain:</strong> {user.szDomainName || '—'} • <strong>Date:</strong> {user.dtCreated ? format(new Date(user.dtCreated), 'dd-MM-yyyy') : '—'}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            <button
                              className="btn btn-sm text-white"
                              style={{ backgroundColor: user.btIsDisabled ? '#0aa375' : 'red', fontSize: '11px', padding: '4px 10px' }}
                              onClick={() => handleAction(user.btIsDisabled ? 'enable_login' : 'disable_login', user)}
                            >
                              {user.btIsDisabled ? 'Enable Login' : 'Disable Login'}
                            </button>
                            <button
                              className="btn btn-sm text-white"
                              style={{ backgroundColor: user.btIsOtpDisabled ? 'red' : '#0aa375', fontSize: '11px', padding: '4px 10px' }}
                              onClick={() => handleAction(user.btIsOtpDisabled ? 'enable_otp' : 'disable_otp', user)}
                            >
                              {user.btIsOtpDisabled ? 'Enable OTP' : 'Disable OTP'}
                            </button>
                            <button
                              className="btn btn-sm text-white"
                              style={{ backgroundColor: user.btShowLoginMessage ? 'red' : '#0aa375', fontSize: '11px', padding: '4px 10px' }}
                              onClick={() => handleAction(user.btShowLoginMessage ? 'disable_msg' : 'enable_msg', user)}
                            >
                              {user.btShowLoginMessage ? 'Disable Msg' : 'Enable Msg'}
                            </button>
                            {user.szUserRole?.toLowerCase() === 'doctor' && (
                              <button
                                className="link-action"
                                style={{ fontSize: '12px', padding: '4px 10px' }}
                                onClick={() => { setSelectedUser(user); setShowAdvanceModal(true); }}
                              >
                                Advance Feature
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{
                    padding: '12px 16px', borderTop: '1px solid #e5e7eb',
                    display: 'flex', justifyContent: 'space-between', 
                    alignItems: 'center', background: '#fafafa', 
                    flexWrap: 'wrap', gap: '12px', fontSize: '12px'
                  }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                      Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
                    </span>

                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button
                        onClick={() => goToPage(page - 1)}
                        disabled={page === 1 || searchLoading}
                        style={{
                          padding: '4px 8px', borderRadius: 6, border: '1px solid #d1fae5',
                          background: page === 1 ? '#f3f4f6' : '#fff',
                          color: page === 1 ? '#9ca3af' : BRAND_GREEN,
                          cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 12
                        }}
                      >‹ Prev</button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => {
                          if (totalPages <= 7) return true;
                          if (p === 1 || p === totalPages) return true;
                          if (p >= page - 2 && p <= page + 2) return true;
                          return false;
                        })
                        .reduce((acc, p, idx, arr) => {
                          if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, idx) =>
                          p === '...' ? (
                            <span key={`ellipsis-${idx}`} style={{ color: '#9ca3af', fontSize: 12, padding: '0 2px' }}>...</span>
                          ) : (
                            <button
                              key={p}
                              onClick={() => goToPage(p)}
                              disabled={searchLoading}
                              style={{
                                width: 28, height: 28, borderRadius: 6, border: '1px solid #d1fae5',
                                background: page === p ? BRAND_GREEN : '#fff',
                                color: page === p ? '#fff' : BRAND_GREEN,
                                fontWeight: page === p ? 700 : 400,
                                cursor: 'pointer', fontSize: 12
                              }}
                            >{p}</button>
                          )
                        )
                      }

                      <button
                        onClick={() => goToPage(page + 1)}
                        disabled={page === totalPages || searchLoading}
                        style={{
                          padding: '4px 8px', borderRadius: 6, border: '1px solid #d1fae5',
                          background: page === totalPages ? '#f3f4f6' : '#fff',
                          color: page === totalPages ? '#9ca3af' : BRAND_GREEN,
                          cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: 12
                        }}
                      >Next ›</button>
                    </div>

                    <div style={{ width: '130px', minWidth: '130px' }}>
                      <Select
                        options={pageSizeOptions}
                        value={pageSizeOptions.find(o => o.value === pageSize)}
                        onChange={handlePageSizeChange}
                        isSearchable={false}
                        isDisabled={searchLoading}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showUserModal && selectedUser && (
        <UserDetailsModal user={selectedUser} domain={domain} onClose={() => { setShowUserModal(false); setSelectedUser(null); }} />
      )}
      {showChangeModal && selectedUser && (
        <ChangePasswordModal user={selectedUser} domain={domain} onClose={() => setShowChangeModal(false)} />
      )}
      {showShowModal && selectedUser && (
        <ShowPasswordModal user={selectedUser} domain={domain} onClose={() => setShowShowModal(false)} />
      )}
      {showAdvanceModal && selectedUser && (
        <AdvanceFeatureModal user={selectedUser} domain={domain} onClose={() => setShowAdvanceModal(false)} />
      )}
      {showDoctorInfoModal && selectedUser && (
        <DoctorInfoModal user={selectedUser} domain={domain} onClose={() => setShowDoctorInfoModal(false)} />
      )}
    </>
  );
}
